import { translate } from "app/language/service"
import { ICsvFileColumn } from "app/model"
import { downloadCsvFile, generateCsvFileContent } from "app/tools/file"
import { formatDate } from "app/util/date/tools/common"
import { TYPE_COORDINATE } from "coordinate/model"
import {
  COORDINATE_CSV_COLUMN_LATITUDE,
  COORDINATE_CSV_COLUMN_LONGITUDE,
  COORDINATE_CSV_COLUMN_NAME,
  COORDINATE_CSV_COLUMN_RADIUS,
  IFormCoordinate,
  SHAPE_CSV_COLUMN_COUNTRY_ID,
  SHAPE_CSV_COLUMN_NAME,
  SHAPE_CSV_COLUMN_NAME_COUNTRY,
  SHAPE_CSV_COLUMN_NAME_NAME,
  SHAPE_CSV_COLUMN_NAME_STATE,
  SHAPE_CSV_COLUMN_NAME_TYPE,
  SHAPE_CSV_COLUMN_STATE_ID,
  SHAPE_CSV_COLUMN_TYPE
} from "form/model"
import {
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState,
  SHAPE_TYPES
} from "shape/model"
import { getCitiesWithoutMetropolitanCities } from "shape/tools/metropolitan"
import { getRadiusInUserUnit } from "./geo-targets-coordinates"

function getGeoTargetTitleFromType(type: string): string {
  switch (type) {
    case TYPE_COORDINATE:
      return translate("campaign.geo-targets.tab.coordinate.title")
    case SHAPE_TYPES.CITY:
      return translate("campaign.geo-targets.shape.city.title")
    case SHAPE_TYPES.POSTAL_CODE:
      return translate("campaign.geo-targets.shape.postal-code.title")
    case SHAPE_TYPES.STATE:
      return translate("campaign.geo-targets.shape.state.title")
    case SHAPE_TYPES.COUNTRY:
      return translate("campaign.geo-targets.shape.country.title")
    case SHAPE_TYPES.METROPOLITAN:
      return translate("campaign.geo-targets.shape.metropolitan.title")
    default: {
      console.error(`Unknown type ${type}`)
      return type
    }
  }
}

export function generateAndDownloadTerritoryToCSVFile(
  cities: IGeoShapeCity[],
  postal_codes: IGeoShapePostalCode[],
  states: IGeoShapeState[],
  countries: IGeoShapeCountry[],
  metropolitans: IGeoShapeMetropolitan[]
): void {
  cities = getCitiesWithoutMetropolitanCities(cities, metropolitans)
  const columns: ICsvFileColumn[] = [
    {
      key: SHAPE_CSV_COLUMN_TYPE,
      label: SHAPE_CSV_COLUMN_NAME_TYPE
    },
    {
      key: SHAPE_CSV_COLUMN_NAME,
      label: SHAPE_CSV_COLUMN_NAME_NAME
    },
    {
      key: SHAPE_CSV_COLUMN_STATE_ID,
      label: SHAPE_CSV_COLUMN_NAME_STATE
    },
    {
      key: SHAPE_CSV_COLUMN_COUNTRY_ID,
      label: SHAPE_CSV_COLUMN_NAME_COUNTRY
    }
  ]
  const fileContent: string = generateCsvFileContent(columns, [
    ...cities.map(c => ({ ...c, type: getGeoTargetTitleFromType(c.type) })),
    ...metropolitans.map(m => ({
      ...m,
      type: getGeoTargetTitleFromType(m.type),
      name: translate(m.labelKey, m.cityIds.length)
    })),
    ...postal_codes.map(p => ({
      ...p,
      type: getGeoTargetTitleFromType(p.type),
      name: p.postal_code
    })),
    ...states.map(s => ({ ...s, type: getGeoTargetTitleFromType(s.type) })),
    ...countries.map(c => ({
      ...c,
      type: getGeoTargetTitleFromType(c.type),
      SHAPE_CSV_COLUMN_COUNTRY_ID: c.alpha3
    }))
  ])
  const fileName: string = `territories-list-${formatDate(new Date())}`
  downloadCsvFile(fileName, fileContent)
}

export function generateAndDownloadCoordinatesToCSVFile(coordinates: IFormCoordinate[]): void {
  const columns: ICsvFileColumn[] = [
    {
      key: SHAPE_CSV_COLUMN_TYPE,
      label: SHAPE_CSV_COLUMN_TYPE
    },
    {
      key: COORDINATE_CSV_COLUMN_NAME,
      label: COORDINATE_CSV_COLUMN_NAME
    },
    {
      key: COORDINATE_CSV_COLUMN_LATITUDE,
      label: COORDINATE_CSV_COLUMN_LATITUDE
    },
    {
      key: COORDINATE_CSV_COLUMN_LONGITUDE,
      label: COORDINATE_CSV_COLUMN_LONGITUDE
    },
    {
      key: COORDINATE_CSV_COLUMN_RADIUS,
      label: COORDINATE_CSV_COLUMN_RADIUS
    }
  ]
  const fileContent: string = generateCsvFileContent(
    columns,
    coordinates.map(c => ({ ...c, radius: getRadiusInUserUnit(c.radius) }))
  )
  const fileName: string = `coordinates-list-${formatDate(new Date())}`
  downloadCsvFile(fileName, fileContent)
}

export function generateAndDownloadGeoTargetToCSVFile(
  coordinates: IFormCoordinate[],
  cities: IGeoShapeCity[],
  postal_codes: IGeoShapePostalCode[],
  states: IGeoShapeState[],
  countries: IGeoShapeCountry[],
  metropolitans: IGeoShapeMetropolitan[]
): void {
  cities = getCitiesWithoutMetropolitanCities(cities, metropolitans)
  const columns: ICsvFileColumn[] = [
    {
      key: SHAPE_CSV_COLUMN_TYPE,
      label: SHAPE_CSV_COLUMN_NAME_TYPE
    },
    {
      key: SHAPE_CSV_COLUMN_NAME,
      label: SHAPE_CSV_COLUMN_NAME_NAME
    },
    {
      key: COORDINATE_CSV_COLUMN_LATITUDE,
      label: COORDINATE_CSV_COLUMN_LATITUDE
    },
    {
      key: COORDINATE_CSV_COLUMN_LONGITUDE,
      label: COORDINATE_CSV_COLUMN_LONGITUDE
    },
    {
      key: COORDINATE_CSV_COLUMN_RADIUS,
      label: COORDINATE_CSV_COLUMN_RADIUS
    },
    {
      key: SHAPE_CSV_COLUMN_STATE_ID,
      label: SHAPE_CSV_COLUMN_NAME_STATE
    },
    {
      key: SHAPE_CSV_COLUMN_COUNTRY_ID,
      label: SHAPE_CSV_COLUMN_NAME_COUNTRY
    }
  ]
  const fileContent: string = generateCsvFileContent(columns, [
    ...coordinates.map(coordinate => ({
      ...coordinate,
      radius: getRadiusInUserUnit(coordinate.radius),
      type: getGeoTargetTitleFromType(coordinate.type)
    })),
    ...cities.map(city => ({ ...city, type: getGeoTargetTitleFromType(city.type) })),
    metropolitans.map(m => ({
      ...m,
      type: getGeoTargetTitleFromType(m.type),
      name: translate(m.labelKey, m.cityIds.length)
    })),
    ...postal_codes.map(postal_code => ({
      ...postal_code,
      type: getGeoTargetTitleFromType(postal_code.type),
      name: postal_code.postal_code
    })),
    ...states.map(state => ({ ...state, type: getGeoTargetTitleFromType(state.type) })),
    ...countries.map(country => ({
      ...country,
      type: getGeoTargetTitleFromType(country.type),
      SHAPE_CSV_COLUMN_COUNTRY_ID: country.alpha3
    }))
  ])
  const fileName: string = `geo-target-list-${formatDate(new Date())}`
  downloadCsvFile(fileName, fileContent)
}
